import { computed } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'

import { AdType } from '@/shared/constants'

import { getBack, signOut } from '@/apps/adok/entities/session'

import {
  toggleFrozen as toggleFrozenRequest,
  togglePayments as togglePaymentsRequest
} from '../api'
import { MarketplaceStatus, OnboardingStatus } from './types'
import type { Account, ContactChannel, PaymentUnderReview } from './types'
import {
  useAcceptTerms,
  useAccountQuery,
  useDismissSystemMessage,
  useRequestCall,
  useRequestMarketplace
} from './queries'

const LOW_BALANCE_FLAG = 'low_balance'
const WITHOUT_INITIAL_PAYMENT_FLAG = 'without_initial_payment'

export function useAccount() {
  const qc = useQueryClient()
  const query = useAccountQuery()

  const requestCallMutation = useRequestCall()
  const requestMarketplaceMutation = useRequestMarketplace()
  const acceptTermsMutation = useAcceptTerms()
  const dismissSystemMessageMutation = useDismissSystemMessage()

  const account = computed<Account | null>(
    () => query.data.value?.data.data ?? null
  )

  const referrerCode = computed(
    () => account.value?.referrerCode?.toString() ?? ''
  )

  const isStaff = computed(() => !!account.value?.staff)

  const marketplaceStatus = computed<MarketplaceStatus>(
    () => account.value?.marketplaceStatus ?? MarketplaceStatus.Disabled
  )

  const canUseMarketplace = computed(
    () => marketplaceStatus.value === MarketplaceStatus.Enabled
  )

  const onboardingStatus = computed<OnboardingStatus>(
    () => account.value?.onboardingStatus ?? OnboardingStatus.NeedCreatives
  )

  const isOnboarded = computed(
    () => onboardingStatus.value === OnboardingStatus.Onboarded
  )

  const onboardingStep = computed(
    () => Number(account.value?.onboardingStep) || 0
  )

  const hadInitialTalk = computed(() => !!account.value?.hadInitialTalk)

  const initialTalkRequested = computed(
    () => !!account.value?.initialTalkRequested
  )

  async function refreshAccount() {
    await query.refetch()
  }

  async function requestCall(channel: ContactChannel) {
    await requestCallMutation.mutateAsync(channel)
  }

  const canRequestMarketplace = computed(
    () => onboardingStatus.value !== OnboardingStatus.NeedCreatives
  )

  async function requestMarketplace() {
    await requestMarketplaceMutation.mutateAsync(undefined)
  }

  const hasTabFlatDeal = computed(() =>
    (account.value?.flatDealAdTypeIds ?? []).includes(AdType.Tab)
  )

  const widgetEnabled = computed(
    () => !!account.value?.withWidget && !!account.value?.widgetData
  )

  const widgetData = computed(() => account.value?.widgetData ?? '')

  const flags = computed(() => account.value?.flags ?? [])

  const hasLowBalance = computed(() => flags.value.includes(LOW_BALANCE_FLAG))

  const awaitsInitialPayment = computed(() =>
    flags.value.includes(WITHOUT_INITIAL_PAYMENT_FLAG)
  )

  const accountId = computed(() => Number(account.value?.id) || 0)

  const accountName = computed(() => account.value?.name ?? '')

  const balanceCents = computed(() => Number(account.value?.balanceCents) || 0)

  /**
   * Ends the session and **empties the cache**, which is the half that is easy to drop: the
   * sign-out is a router push, so the document survives it and every query still holds the
   * previous account's rows — including the account the guard reads, which would keep
   * waving the visitor through for a full `staleTime`.
   */
  async function logOut() {
    await signOut()
    qc.clear()
  }

  async function stopImpersonating() {
    await getBack()
  }

  const frozen = computed(() => !!account.value?.frozen)

  const allowedPayments = computed(() => String(account.value?.allowedPayments))

  async function toggleFrozen() {
    await toggleFrozenRequest()
    await refreshAccount()
  }

  async function togglePayments() {
    await togglePaymentsRequest()
    await refreshAccount()
  }

  const termsAccepted = computed(() => !!account.value?.termsAccepted)

  async function acceptTerms() {
    await acceptTermsMutation.mutateAsync(undefined)
  }

  const systemMessage = computed(() =>
    account.value?.showSystemMessage ? (account.value?.systemMessage ?? '') : ''
  )

  async function dismissSystemMessage() {
    await dismissSystemMessageMutation.mutateAsync(undefined)
  }

  const paymentsUnderReview = computed<PaymentUnderReview[]>(() =>
    (account.value?.paymentsUnderReview ?? [])
      .filter((payment) => payment.new_payment)
      .map((payment) => ({
        paymentSystem: payment.payment_system ?? '',
        amount: payment.amount
      }))
  )

  return {
    referrerCode,
    isStaff,
    marketplaceStatus,
    onboardingStatus,
    isOnboarded,
    onboardingStep,
    hadInitialTalk,
    initialTalkRequested,
    requestCall,
    refreshAccount,
    canUseMarketplace,
    canRequestMarketplace,
    requestMarketplace,
    hasTabFlatDeal,
    widgetEnabled,
    widgetData,
    hasLowBalance,
    awaitsInitialPayment,
    accountId,
    accountName,
    balanceCents,
    logOut,
    stopImpersonating,
    frozen,
    allowedPayments,
    toggleFrozen,
    togglePayments,
    termsAccepted,
    acceptTerms,
    systemMessage,
    dismissSystemMessage,
    paymentsUnderReview
  }
}
